import { useEffect, useState } from 'react'
import { Avatar } from '../components/Avatar'
import { buehneWaehlen, nehmeSchaufenster } from '../lib/api'
import { IntroRemotePage } from './IntroRemotePage'

type Stand =
  | { art: 'laedt' }
  | { art: 'steuert'; token: string }
  | { art: 'belegt'; freiAb: number }
  | { art: 'abgelaufen' }
  | { art: 'fertig' }
  | { art: 'fehler' }

/** Das Steuer-Token überlebt ein Neuladen der Seite, nicht aber das Schließen des Tabs. */
const merkSchluessel = (code: string) => `k4ia:schaufenster:${code}`

function gemerkt(code: string): string | null {
  try {
    return sessionStorage.getItem(merkSchluessel(code))
  } catch {
    return null
  }
}

function merken(code: string, token: string | null): void {
  try {
    if (token) sessionStorage.setItem(merkSchluessel(code), token)
    else sessionStorage.removeItem(merkSchluessel(code))
  } catch {
    // Privater Modus o. Ä.: dann eben ohne Neuladen-Schutz.
  }
}

/**
 * `/schaufenster/go/<code>`: die Adresse im QR-Code des Schaufensters.
 *
 * Übernimmt die Steuerung, sobald die Seite aufgeht — der Code wechselt alle
 * paar Minuten, wer ihn scannt, steht also davor. Steuert gerade jemand
 * anderes, steht hier, wann das Fenster wieder frei ist. Danach ist es die
 * gewohnte Fernbedienung (IntroRemotePage), mit „Fertig“ statt „Vorführung
 * beenden“. Nach zwei Minuten ohne Bedienung ist das Fenster von selbst frei.
 */
export function SchaufensterRemotePage({ code }: { code: string }) {
  useState(() => buehneWaehlen('schaufenster'))
  const [stand, setStand] = useState<Stand>(() => {
    const token = gemerkt(code)
    return token ? { art: 'steuert', token } : { art: 'laedt' }
  })
  const [jetzt, setJetzt] = useState(Date.now())

  const nehmen = async () => {
    setStand({ art: 'laedt' })
    try {
      const r = await nehmeSchaufenster(code)
      if (r.art === 'ok') {
        merken(code, r.token)
        setStand({ art: 'steuert', token: r.token })
      } else if (r.art === 'belegt') {
        setStand({ art: 'belegt', freiAb: Date.now() + r.freiInSek * 1000 })
      } else {
        setStand({ art: 'abgelaufen' })
      }
    } catch {
      setStand({ art: 'fehler' })
    }
  }

  useEffect(() => {
    if (stand.art === 'laedt') void nehmen()
    // Einmal beim Öffnen; danach nur auf Knopfdruck.
  }, [])

  // Belegt: Countdown, dann von selbst noch einmal versuchen.
  useEffect(() => {
    if (stand.art !== 'belegt') return
    const id = setInterval(() => {
      setJetzt(Date.now())
      if (Date.now() >= stand.freiAb) void nehmen()
    }, 1000)
    return () => clearInterval(id)
  }, [stand])

  if (stand.art === 'steuert') {
    return (
      <IntroRemotePage
        token={stand.token}
        modus="schaufenster"
        onFertig={(grund) => {
          merken(code, null)
          setStand(grund === 'abgegeben' ? { art: 'fertig' } : { art: 'abgelaufen' })
        }}
      />
    )
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-bg p-8 text-center">
      <Avatar
        state={stand.art === 'laedt' ? 'thinking' : stand.art === 'fehler' ? 'error' : 'idle'}
        size={160}
        showLabel={false}
      />
      <div className="flex w-full max-w-sm flex-col items-center gap-4">
        {stand.art === 'laedt' && <p className="text-xl">Einen Moment …</p>}
        {stand.art === 'belegt' && (
          <>
            <p className="text-xl">Gerade steuert jemand anderes das Schaufenster.</p>
            <p className="text-text-muted" aria-live="polite">
              Spätestens in {Math.max(1, Math.ceil((stand.freiAb - jetzt) / 1000))} Sekunden bist du
              dran — die Seite versucht es dann von selbst.
            </p>
          </>
        )}
        {stand.art === 'abgelaufen' && (
          <>
            <p className="text-xl">Deine Zeit am Schaufenster ist um.</p>
            <p className="text-text-muted">
              Scan den QR-Code im Fenster einfach noch einmal — er wechselt alle paar Minuten.
            </p>
          </>
        )}
        {stand.art === 'fertig' && (
          <>
            <p className="text-xl">Danke fürs Vorbeischauen!</p>
            <p className="text-text-muted">
              Mehr über das J4F VisionLab erzählt dir K4ia jederzeit wieder am Schaufenster.
            </p>
          </>
        )}
        {stand.art === 'fehler' && (
          <>
            <p className="text-xl">Das hat nicht geklappt.</p>
            <button
              onClick={() => void nehmen()}
              className="btn-primary w-full rounded-2xl px-8 py-4 text-lg font-semibold"
            >
              Noch einmal versuchen
            </button>
          </>
        )}
      </div>
    </main>
  )
}
